import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Heart,
  Save,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ValuesManagement = () => {
  const [values, setValues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingValue, setEditingValue] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    subtitle: '',
    description: '',
    principles: [],
    methods: [],
    outcomes: ''
  });

  useEffect(() => {
    loadValues();
  }, []);

  const loadValues = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/values/`, {
        withCredentials: true
      });
      setValues(response.data);
    } catch (error) {
      console.error('Error loading values:', error);
      toast.error('Failed to load core values');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      subtitle: '',
      description: '',
      principles: [],
      methods: [],
      outcomes: ''
    });
    setEditingValue(null);
  };

  const handleEdit = (value) => {
    setEditingValue(value);
    setFormData({
      name: value.name,
      slug: value.slug,
      subtitle: value.subtitle,
      description: value.description,
      principles: value.principles || [],
      methods: value.methods || [],
      outcomes: (value.outcomes || []).join('\n')
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      outcomes: formData.outcomes.split('\n').map(item => item.trim()).filter(item => item)
    };

    try {
      if (editingValue) {
        await axios.put(`${BACKEND_URL}/api/values/${editingValue.id}/`, submitData, {
          withCredentials: true
        });
        toast.success('Core value updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/values/`, submitData, {
          withCredentials: true
        });
        toast.success('Core value created successfully');
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadValues();
    } catch (error) {
      console.error('Error saving value:', error);
      toast.error('Failed to save core value');
    }
  };

  const handleDelete = async (valueId) => {
    if (!window.confirm('Are you sure you want to delete this core value?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/values/${valueId}/`, {
        withCredentials: true
      });
      toast.success('Core value deleted successfully');
      loadValues();
    } catch (error) {
      console.error('Error deleting value:', error);
      toast.error('Failed to delete core value');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-vincotek-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading core values...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-vincotek-dark">Core Values</h2>
          <p className="text-gray-600">Manage company core values and their content</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-vincotek-blue hover:bg-blue-600 text-white"
              onClick={() => resetForm()}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Value
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingValue ? 'Edit Core Value' : 'Add New Core Value'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Value Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Innovation"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Slug</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="auto-generated-from-name"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  placeholder="e.g., Creating Smart Solutions for Real-World Challenges"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Main Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={6}
                  placeholder="Detailed description of this core value..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="outcomes">Key Outcomes (one per line)</Label>
                <Textarea
                  id="outcomes"
                  value={formData.outcomes}
                  onChange={(e) => handleInputChange('outcomes', e.target.value)}
                  placeholder="Outcome 1&#10;Outcome 2&#10;Outcome 3"
                  rows={6}
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <Button type="submit" className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  {editingValue ? 'Update Value' : 'Add Value'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Values Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {values.map((value) => (
          <Card key={value.id} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-2">
                    {value.name}
                  </h3>
                  <p className="text-vincotek-blue font-medium text-sm mb-3">
                    {value.subtitle}
                  </p>
                  <p className="text-gray-600 text-sm line-clamp-4">
                    {value.description}
                  </p>
                </div>
                <div className="flex space-x-1 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(value)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(value.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {value.outcomes && value.outcomes.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-600 mb-2">Key Outcomes:</p>
                  <ul className="space-y-1">
                    {value.outcomes.slice(0, 3).map((outcome, index) => (
                      <li key={index} className="text-xs text-gray-600 flex items-start">
                        <span className="w-1 h-1 bg-vincotek-blue rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        {outcome}
                      </li>
                    ))}
                    {value.outcomes.length > 3 && (
                      <li className="text-xs text-gray-500">
                        +{value.outcomes.length - 3} more outcomes
                      </li>
                    )}
                  </ul>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Slug: /{value.slug}</span>
                  <span>{new Date(value.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {values.length === 0 && (
        <Card className="bg-white border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No core values found
            </h3>
            <p className="text-gray-500 mb-6">
              Add your first core value to get started.
            </p>
            <Button 
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
              className="bg-vincotek-blue hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Value
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ValuesManagement;